import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Send, ArrowLeft, Sparkles, User, Bot,
  Briefcase, CheckCircle, ArrowRight, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { getAuthToken } from '@/utils/secureStorage';

const API = process.env.REACT_APP_BACKEND_URL;

export default function IntakeWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState({
    prior_work_experience: '',
    motivation_for_change: '',
    barriers_identified: [],
    aspirational_career: '',
    preferred_sector: null
  });
  const [step, setStep] = useState(0);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  // Check enrollment status
  useEffect(() => {
    const checkEnrollment = async () => {
      if (!user) return;
      try {
        const token = getAuthToken();
        const res = await fetch(`${API}/api/workforce/participant`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.enrolled) {
            setEnrolled(true);
          }
        }
      } catch (err) {
        console.error('Error checking enrollment:', err);
      }
    };
    checkEnrollment();
  }, [user]);

  // Initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Welcome. I'm your DowUrk Workforce Guide.

I'm here to help you find a path to stable, meaningful work. Let's start by getting to know you a bit.

**Tell me: What kind of work have you done before—paid or unpaid? What did you like or dislike about it?**

Take your time. There's no wrong answer here.`
      }]);
    }
  }, [messages.length]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    if (!user) {
      toast.error('Please log in to continue');
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    // Store responses based on step
    const newData = { ...enrollmentData };
    if (step === 0) newData.prior_work_experience = userMessage;
    else if (step === 1) newData.motivation_for_change = userMessage;
    else if (step === 2) {
      // Parse barriers from response
      const barrierKeywords = {
        'child': 'childcare',
        'kid': 'childcare',
        'daycare': 'childcare',
        'transport': 'transportation',
        'car': 'transportation',
        'bus': 'transportation',
        'ride': 'transportation',
        'housing': 'housing',
        'home': 'housing',
        'rent': 'housing',
        'legal': 'legal',
        'record': 'legal',
        'mental': 'mental_health',
        'anxiety': 'mental_health',
        'depression': 'mental_health',
        'stress': 'mental_health'
      };
      const barriers = [];
      Object.entries(barrierKeywords).forEach(([keyword, barrier]) => {
        if (userMessage.toLowerCase().includes(keyword) && !barriers.includes(barrier)) {
          barriers.push(barrier);
        }
      });
      newData.barriers_identified = barriers;
    }
    else if (step === 3) newData.aspirational_career = userMessage;
    
    setEnrollmentData(newData);
    setStep(prev => prev + 1);

    setSending(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          context: 'intake',
          session_id: sessionId
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        throw new Error('Failed to get response');
      }
    } catch (err) {
      toast.error('Failed to get response. Please try again.');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Let's try again in a moment." 
      }]);
    } finally {
      setSending(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      toast.error('Please log in to enroll');
      return;
    }

    setEnrolling(true);
    try {
      const token = getAuthToken();
      const res = await fetch(`${API}/api/workforce/enroll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(enrollmentData)
      });

      if (res.ok) {
        toast.success('Welcome to the program! Let\'s get started.');
        setEnrolled(true);
        setTimeout(() => navigate('/workforce/pathway'), 1500);
      } else {
        const err = await res.json();
        if (err.detail?.includes('Already enrolled')) {
          toast.info('You\'re already enrolled! Redirecting...');
          setTimeout(() => navigate('/workforce/pathway'), 1000);
        } else {
          throw new Error(err.detail || 'Failed to enroll');
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (enrolled) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-2">You're Enrolled!</h2>
            <p className="text-slate-600 mb-6">Your journey begins now. Let's check your pathway.</p>
            <Link to="/workforce/pathway">
              <Button className="bg-blue-600 hover:bg-blue-700">
                View My Pathway <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50/30" data-testid="intake-wizard">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-8">
        <div className="container mx-auto px-4">
          <Link to="/workforce" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Workforce Hub
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <MessageCircle className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Needs Assessment</h1>
              <p className="text-blue-100">Let's find the right path for you</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        <Card className="min-h-[60vh] flex flex-col">
          <CardContent className="flex-1 overflow-y-auto py-6 space-y-4">
            <AnimatePresence>
              {messages.map((msg, idx) => (
                <motion.div
                  key={`item-${idx}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-5 w-5 text-blue-600" />
                    </div>
                  )}
                  <div className={`max-w-[80%] p-4 rounded-2xl ${
                    msg.role === 'user' 
                      ? 'bg-blue-600 text-white rounded-br-sm' 
                      : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                  }`}>
                    <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-slate-600" />
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            
            {sending && (
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-blue-600" />
                </div>
                <div className="bg-slate-100 p-4 rounded-2xl rounded-bl-sm">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Input Area */}
          <div className="border-t p-4">
            {step >= 4 && !enrolled ? (
              <div className="text-center">
                <p className="text-slate-600 mb-4">Ready to begin your journey?</p>
                <Button 
                  onClick={handleEnroll}
                  disabled={enrolling}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {enrolling ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Enrolling...</>
                  ) : (
                    <><CheckCircle className="h-4 w-4 mr-2" /> Enroll in Program</>
                  )}
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Type your response..."
                  className="min-h-[60px] resize-none"
                  disabled={!user || sending}
                />
                <Button 
                  onClick={handleSend}
                  disabled={!input.trim() || sending || !user}
                  className="bg-blue-600 hover:bg-blue-700 px-4"
                >
                  <Send className="h-5 w-5" />
                </Button>
              </div>
            )}
            
            {!user && (
              <p className="text-center text-sm text-slate-500 mt-2">
                <Link to="/login" className="text-blue-600 hover:underline">Log in</Link> to continue the assessment
              </p>
            )}
          </div>
        </Card>

        {/* Progress Indicator */}
        <div className="flex justify-center gap-2 mt-4">
          {[0, 1, 2, 3].map(i => (
            <div
              key={`item-${i}`}
              className={`w-3 h-3 rounded-full transition-colors ${
                i < step ? 'bg-blue-600' : i === step ? 'bg-blue-400' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
